import { z } from 'zod';

const hhmm = z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/);

export const dayHoursSchema = z.object({
  open: hhmm,
  close: hhmm,
  closed: z.boolean(),
});

export const WEEKDAYS = ['mon', 'tue', 'wed', 'thu', 'fri', 'sat', 'sun'] as const;
export type Weekday = (typeof WEEKDAYS)[number];
export const WEEKDAY_LABELS_FR: Record<Weekday, string> = {
  mon: 'Lundi',
  tue: 'Mardi',
  wed: 'Mercredi',
  thu: 'Jeudi',
  fri: 'Vendredi',
  sat: 'Samedi',
  sun: 'Dimanche',
};
export const WEEKDAY_LABELS_EN: Record<Weekday, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};
export const WEEKDAY_LABELS = { en: WEEKDAY_LABELS_EN, fr: WEEKDAY_LABELS_FR } as const;

export const restaurantSettingsSchema = z.object({
  description: z.string().max(500).default(''),
  phone: z.string().max(40).default(''),
  email: z.string().max(120).default(''),
  logoUrl: z.string().max(500).default('/brand/logo-transparent.png'),
  heroImageUrl: z.string().max(500).default(''),
  location: z.object({ lat: z.number().min(-90).max(90), lng: z.number().min(-180).max(180) }),
  hours: z.object({
    mon: dayHoursSchema,
    tue: dayHoursSchema,
    wed: dayHoursSchema,
    thu: dayHoursSchema,
    fri: dayHoursSchema,
    sat: dayHoursSchema,
    sun: dayHoursSchema,
  }),
  acceptOrdersWhenClosed: z.boolean().default(true),
  deliveryEnabled: z.boolean().default(true),
  pickupEnabled: z.boolean().default(true),
  deliveryRadiusKm: z.number().min(0.5).max(50).default(8),
  deliveryFeeCents: z.number().int().min(0).max(10_000).default(499),
  minimumOrderCents: z.number().int().min(0).max(100_000).default(1500),
  taxRatePercent: z.number().min(0).max(50).default(14.975),
  prepTimeMinutes: z.number().int().min(1).max(180).default(20),
  qrTtlMinutes: z.number().int().min(5).max(24 * 60).default(180),
  notifications: z
    .object({
      kitchenSound: z.boolean().default(true),
      browserNotifications: z.boolean().default(false),
      notifyCustomerByEmail: z.boolean().default(false),
    })
    .default({}),
  promo: z
    .object({
      enabled: z.boolean().default(true),
      title: z.string().max(80).default(''),
      subtitle: z.string().max(120).default(''),
      itemId: z.string().max(60).optional(),
    })
    .default({}),
});

export type RestaurantSettings = z.infer<typeof restaurantSettingsSchema>;
export const settingsPatchSchema = restaurantSettingsSchema.partial();

export interface OpenStatus {
  open: boolean;
  todayLabel: string;
  opensAt?: string;
  closesAt?: string;
}

/** Computes open/closed in the restaurant's timezone. */
export function computeOpenStatus(settings: Pick<RestaurantSettings, 'hours'>, timezone: string, now = new Date()): OpenStatus {
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(now);
  const wd = parts.find((p) => p.type === 'weekday')?.value.toLowerCase().slice(0, 3) as Weekday;
  const hh = parts.find((p) => p.type === 'hour')?.value ?? '00';
  const mm = parts.find((p) => p.type === 'minute')?.value ?? '00';
  const cur = `${hh}:${mm}`;
  const day = settings.hours[wd];
  if (!day || day.closed) return { open: false, todayLabel: 'Fermé aujourd’hui' };
  const open = day.close > day.open ? cur >= day.open && cur < day.close : cur >= day.open || cur < day.close;
  return {
    open,
    todayLabel: `${day.open.replace(':', ' h ')} – ${day.close.replace(':', ' h ')}`,
    opensAt: day.open,
    closesAt: day.close,
  };
}
